/**
 * Attribution capture — preserves the visitor's source/path so the
 * Opportunity Signals admin queue and Stripe checkout metadata know
 * where a lead actually came from.
 *
 * Captures any of: ?src, ?ref, ?utm_source, ?utm_medium,
 * ?utm_campaign, ?utm_content, ?utm_term, ?via, ?from.
 *
 * Persistence model:
 *   - First-touch attribution is stamped once and never overwritten,
 *     so the original channel that introduced the visitor stays
 *     attributable even after they navigate internally.
 *   - Last-touch attribution refreshes on every entry that carries
 *     attribution params, so re-engagements (a follow-up email,
 *     a podcast appearance) are also captured.
 *   - Both are merged into the existing signal-path engine state
 *     (`am_signal_path_v1` → `.attribution` field) so the admin
 *     Opportunity Signals layer can derive a single source line
 *     per lead without any new collection.
 */
const ATTRIBUTION_KEY = "am_attribution_v1";
const SIGNAL_PATH_KEY = "am_signal_path_v1";

const TRACKED_PARAMS = [
  "src",
  "ref",
  "via",
  "from",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
];

const readJSON = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeJSON = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* localStorage may be disabled — non-fatal */
  }
};

/** Read the visitor's current persisted attribution snapshot. */
export const getAttribution = () => readJSON(ATTRIBUTION_KEY) || null;

/** Mirror the attribution into the signal-path engine state so admin
 *  Opportunity Signals see it on a single read of am_signal_path_v1. */
const mirrorIntoSignalPath = (attribution) => {
  const existing = readJSON(SIGNAL_PATH_KEY) || {};
  existing.attribution = attribution;
  writeJSON(SIGNAL_PATH_KEY, existing);
};

/**
 * Parse current URL params and persist any attribution params we find.
 * Idempotent: safe to call on every page load. Returns the current
 * attribution snapshot (existing or newly created), or null if none.
 */
export const captureAttribution = () => {
  let params;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return getAttribution();
  }

  const found = {};
  TRACKED_PARAMS.forEach((p) => {
    const v = params.get(p);
    if (v) found[p] = v.slice(0, 120); // cap to defend localStorage size
  });

  const referrer = (document.referrer || "").slice(0, 200);
  const landingPath = `${window.location.pathname}${window.location.search}`.slice(0, 240);

  // Always preserve referrer + landing on first touch so we have some
  // shape even if no explicit param was passed.
  const hasParams = Object.keys(found).length > 0;
  const existing = getAttribution();

  if (!existing) {
    const snapshot = {
      first_touch: {
        ...found,
        referrer,
        landing_path: landingPath,
        ts: new Date().toISOString(),
      },
      last_touch: hasParams
        ? { ...found, referrer, landing_path: landingPath, ts: new Date().toISOString() }
        : null,
    };
    writeJSON(ATTRIBUTION_KEY, snapshot);
    mirrorIntoSignalPath(snapshot);
    return snapshot;
  }

  // If we already have first_touch, only update last_touch on a fresh
  // attribution params hit.
  if (hasParams) {
    const updated = {
      ...existing,
      last_touch: {
        ...found,
        referrer,
        landing_path: landingPath,
        ts: new Date().toISOString(),
      },
    };
    writeJSON(ATTRIBUTION_KEY, updated);
    mirrorIntoSignalPath(updated);
    return updated;
  }

  // Make sure the signal-path mirror is fresh even if no new params.
  mirrorIntoSignalPath(existing);
  return existing;
};

/**
 * Returns a short, human-readable source string for use in
 * `source_context` on consultation submissions. Falls back to a
 * referrer hint or null when nothing was tracked.
 */
export const getAttributionContextLine = () => {
  const a = getAttribution();
  if (!a) return null;
  const t = a.last_touch || a.first_touch || {};
  const parts = [];
  if (t.src) parts.push(`src=${t.src}`);
  if (t.ref) parts.push(`ref=${t.ref}`);
  if (t.utm_source) parts.push(`utm_source=${t.utm_source}`);
  if (t.utm_medium) parts.push(`utm_medium=${t.utm_medium}`);
  if (t.utm_campaign) parts.push(`utm_campaign=${t.utm_campaign}`);
  if (parts.length === 0 && t.referrer) {
    try {
      const host = new URL(t.referrer).host;
      if (host) parts.push(`referrer=${host}`);
    } catch {
      /* invalid url — ignore */
    }
  }
  return parts.length > 0 ? parts.join(" · ") : null;
};

/**
 * Returns a metadata-safe object suitable for Stripe checkout
 * metadata. Stripe metadata is shallow string→string, so we flatten
 * the most useful fields.
 */
export const getAttributionForCheckout = () => {
  const a = getAttribution();
  if (!a) return {};
  const t = a.last_touch || a.first_touch || {};
  const out = {};
  if (t.src) out.attr_src = t.src;
  if (t.ref) out.attr_ref = t.ref;
  if (t.utm_source) out.attr_utm_source = t.utm_source;
  if (t.utm_medium) out.attr_utm_medium = t.utm_medium;
  if (t.utm_campaign) out.attr_utm_campaign = t.utm_campaign;
  if (t.utm_content) out.attr_utm_content = t.utm_content;
  if (t.referrer) out.attr_referrer = t.referrer.slice(0, 150);
  return out;
};
